/**
 * P-06/P-07 授权码批次管理（docs/03-UIUX.md §5）。
 *
 * 按产品选择，展示该产品的所有批次。支持新建批次（三步向导）。
 */
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '../icons/Icon';
import { EmptyFirstRun, ErrorBlock } from '../components/DataState';
import { describeError } from '../api/client';
import { exportCodeBatchCsv, listProducts, listCodeBatches, listPlans } from '../api/endpoints';
import { useAsync } from '../lib/useAsync';
import { ICON_SIZE } from '../tokens';
import { formatDate, formatCount } from '../lib/format';
import type { Plan, Product, CodeBatch } from '../api/types';

export function BatchesPage() {
  const products = useAsync(useCallback(() => listProducts(), []), []);
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [showCreate, setShowCreate] = useState(false);

  const batches = useAsync(
    useCallback(() => listCodeBatches(selectedProductId || undefined), [selectedProductId]),
    [selectedProductId],
  );

  const productList: readonly Product[] = products.state.kind === 'ready' ? products.state.data.items : [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">授权码批次</h1>
          <p className="page-head__sub">
            按产品管理授权码批次。生成批次时明文只存在于内存中，导出后不可再次获取。
          </p>
        </div>
        <div className="page-head__actions">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setShowCreate(true)}
          >
            <Icon name="plus" size={ICON_SIZE.inline} />
            新建批次
          </button>
        </div>
      </div>

      {/* 产品选择器 */}
      <div className="filter-bar">
        <div className="filter-bar__group">
          <label className="filter-bar__label">产品</label>
          <select
            className="filter-bar__select"
            value={selectedProductId}
            onChange={(e) => { setSelectedProductId(e.target.value); }}
          >
            <option value="">全部产品</option>
            {productList.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* 批次列表 */}
      {batches.state.kind === 'failed' ? (
        <div className="table-frame">
          <ErrorBlock message={describeError(batches.state.error, '加载批次列表')} onRetry={batches.reload} />
        </div>
      ) : batches.state.kind === 'loading' ? (
        <div className="table-frame">
          <table className="table">
            <TableSkeleton rows={5} columns={7} />
          </table>
        </div>
      ) : batches.state.data.items.length === 0 ? (
        <div className="table-frame">
          <EmptyFirstRun
            icon="boxes"
            description={selectedProductId ? '该产品暂无授权码批次。创建第一个批次来发卡。' : '暂无授权码批次。创建第一个批次来发卡。'}
            action={
              <button type="button" className="btn btn--primary" onClick={() => setShowCreate(true)}>
                <Icon name="plus" size={ICON_SIZE.inline} />
                新建批次
              </button>
            }
          />
        </div>
      ) : (
        <div className="table-frame">
          <table className="table">
            <thead>
              <tr>
                <th>批次号</th>
                <th>产品</th>
                <th>数量</th>
                <th>已导出</th>
                <th>状态</th>
                <th>备注</th>
                <th>创建时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {batches.state.data.items.map((batch) => (
                <BatchRow key={batch.id} batch={batch} products={productList} onExported={() => batches.reload()} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateBatchWizard
          initialProductId={selectedProductId || undefined}
          products={productList}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); batches.reload(); }}
        />
      )}
    </>
  );
}

interface BatchRowProps {
  readonly batch: CodeBatch;
  readonly products: readonly Product[];
  readonly onExported: () => void;
}

function triggerCsvDownload(csv: string, filename: string) {
  const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function BatchRow({ batch, products, onExported }: BatchRowProps) {
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const alreadyExported = (batch.export_count ?? 0) > 0;

  const handleExport = async () => {
    if (alreadyExported || exporting) return;
    setExporting(true);
    setExportError(null);
    try {
      const csv = await exportCodeBatchCsv(batch.id);
      triggerCsvDownload(csv, `batch-${batch.batch_no}.csv`);
      onExported();
    } catch (e) {
      setExportError(e instanceof Error ? e.message : '导出失败');
    } finally {
      setExporting(false);
    }
  };

  return (
    <tr key={batch.id}>
      <td>
        <Link to={`/batches/${batch.id}`} className="link-mono" title="查看批次详情">
          {batch.batch_no}
        </Link>
      </td>
      <td>{products.find((p) => p.id === batch.product_id)?.name ?? batch.product_id.slice(0, 8)}</td>
      <td>{formatCount(batch.quantity)}</td>
      <td>{alreadyExported ? formatCount(batch.export_count ?? 0) : '-'}</td>
      <td>
        <span className={`status-dot status-dot--${alreadyExported ? 'archived' : 'active'}`} />
        {alreadyExported ? '已导出' : '可用'}
      </td>
      <td className="text-muted">{batch.note ?? '-'}</td>
      <td>{formatDate(batch.created_at)}</td>
      <td>
        <button
          type="button"
          className="btn btn--quiet"
          disabled={alreadyExported || exporting}
          onClick={handleExport}
          title={alreadyExported ? '该批次已导出过，明文不可再次获取' : '一次性导出 CSV'}
        >
          <Icon name="download" size={ICON_SIZE.inline} />
          {exporting ? '导出中…' : alreadyExported ? '已导出' : '导出 CSV'}
        </button>
        {exportError && <span className="text-muted" style={{ fontSize: 12 }}>{exportError}</span>}
      </td>
    </tr>
  );
}

function TableSkeleton({ rows, columns }: { rows: number; columns: number }) {
  return (
    <tbody aria-busy="true">
      {Array.from({ length: rows }, (_, i) => (
        <tr key={i}>
          {Array.from({ length: columns }, (_, j) => (
            <td key={j}><div className="skeleton-bar" style={{ width: `${[70, 45, 55, 40, 60, 50][j % 6]}%` }} /></td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

interface CreateBatchWizardProps {
  readonly initialProductId?: string;
  readonly products: readonly Product[];
  readonly onClose: () => void;
  readonly onCreated: () => void;
}

function CreateBatchWizard({ initialProductId, products, onClose, onCreated }: CreateBatchWizardProps) {
  const [selectedProductId, setSelectedProductId] = useState(initialProductId ?? '');
  const plans = useAsync(useCallback(() => listPlans(selectedProductId || undefined), [selectedProductId]), [selectedProductId]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [quantity, setQuantity] = useState(100);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const planList: readonly Plan[] = plans.state.kind === 'ready' ? plans.state.data.items : [];

  const handleSubmit = async () => {
    if (!selectedProductId || !selectedPlanId || quantity < 1) return;
    setSubmitting(true);
    setError(null);
    try {
      const { createCodeBatch } = await import('../api/endpoints');
      const ik = `batch-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      await createCodeBatch(selectedProductId, selectedPlanId, quantity, { note, idempotencyKey: ik });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : '创建失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="panel-overlay" onClick={onClose}>
      <div className="panel" onClick={(e) => e.stopPropagation()}>
        <div className="panel__head">
          <h2 className="panel__title">新建批次</h2>
          <button type="button" className="btn btn--quiet" onClick={onClose}>
            <Icon name="x" size={ICON_SIZE.control} />
          </button>
        </div>
        <div className="panel__body">
          <div className="field">
            <label>产品</label>
            <select value={selectedProductId} onChange={(e) => { setSelectedProductId(e.target.value); setSelectedPlanId(''); }}>
              <option value="">选择产品…</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div className="field">
            <label>套餐</label>
            {plans.state.kind === 'loading' ? (
              <div className="skeleton-bar" style={{ width: '100%', height: 44 }} />
            ) : planList.length === 0 ? (
              <p className="text-muted">该产品暂无套餐</p>
            ) : (
              <select value={selectedPlanId} onChange={(e) => setSelectedPlanId(e.target.value)}>
                <option value="">选择套餐</option>
                {planList.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} ({p.code})</option>
                ))}
              </select>
            )}
          </div>

          <div className="field">
            <label>数量</label>
            <input
              type="number"
              min={1}
              max={10000}
              value={quantity}
              onChange={(e) => setQuantity(Math.min(10000, Math.max(1, Number(e.target.value) || 1)))}
            />
          </div>

          <div className="field">
            <label>备注（可选）</label>
            <input
              type="text"
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="内部备注，不对外展示"
            />
          </div>

          {error && (
            <p className="login-error">
              <Icon name="octagon-x" size={ICON_SIZE.inline} />
              {error}
            </p>
          )}

          <button
            type="button"
            className="btn btn--primary"
            disabled={submitting || !selectedProductId || !selectedPlanId || quantity < 1}
            onClick={handleSubmit}
          >
            {submitting ? '创建中…' : '生成批次'}
          </button>
        </div>
      </div>
    </div>
  );
}